import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useServiceRequests } from '@/services/api/hooks/serviceRequests';
import { ServiceRequestDto } from '@/services/api/contracts/serviceRequests';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Modal } from '@/components/modal/Modal';
import { useModal } from '@/components/modal/useModal';
import { ServiceRequestPayloadViewer } from './ServiceRequestPayloadViewer';
import { Eye } from 'lucide-react';

const columnHelper = createColumnHelper<ServiceRequestDto>();

export const SubmittedRequests: FC = () => {
  const { partnerId, companyId } = useParams();

  const [data, { isLoading, error }] = useServiceRequests(partnerId, companyId);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequestDto | null>(null);
  const modal = useModal();

  const handleViewRequest = (request: ServiceRequestDto) => {
    setSelectedRequest(request);
    modal.open();
  };

  const tableData = useMemo(() => data || [], [data]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: info => (
          <button
            onClick={() => handleViewRequest(info.row.original)}
            className="p-1.5 text-gray-400 hover:text-primary-std hover:bg-gray-100 rounded-md transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
        )
      }),
      columnHelper.display({
        id: 'customerReference',
        header: 'Customer Reference',
        cell: info => {
          const req = info.row.original;
          const ref = req.customerReference || req.payload?.customerReference || req.payload?.deliveryDetails?.customerReference || req.payload?.logisticsDetails?.customerReference || '-';
          return ref;
        }
      }),
      columnHelper.accessor('createdAt', {
        header: 'Date Submitted',
        cell: info => {
          try {
            const date = new Date(info.getValue());
            const dateStr = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date).toUpperCase();
            const timeStr = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' }).format(date);
            return `${dateStr} ${timeStr}`;
          } catch {
            return info.getValue();
          }
        }
      }),
      columnHelper.accessor('requestType', {
        header: 'Request Type',
        cell: info => info.getValue()
      }),
      columnHelper.accessor('requestedBy', {
        header: 'Submitted By',
        cell: info => info.getValue()
      })
    ],
    [] // eslint-disable-next-line react-hooks/exhaustive-deps
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const breadcrumbs: NavPage[] = [
    {
      name: 'Management',
      to: ''
    },
    {
      name: 'Submitted Requests',
      to: ''
    }
  ];

  if (!partnerId || !companyId) throw new Error('Missing route params');

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Submitted Requests</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto p-10">
        {isLoading ? (
          <div className="animate-pulse flex flex-col space-y-4">
            <div className="h-10 bg-gray-200 rounded w-full"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
          </div>
        ) : error ? (
          <div className="text-red-500">Failed to load service requests.</div>
        ) : tableData.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            No service requests found.
          </div>
        ) : (
          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal {...modal}>
        {selectedRequest && (
          <div className="relative max-h-[70vh] flex flex-col rounded-lg overflow-hidden bg-white max-w-4xl w-full mx-auto shadow-2xl">
            <Modal.Controls {...modal} />
            <div className="overflow-y-auto w-full h-full flex flex-col">
              <ServiceRequestPayloadViewer request={selectedRequest} />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
